/**
 * Custom hook for standardized vehicle statistics access and formatting
 * Centralizes all vehicle stat calculations to prevent duplication
 */

const useVehicleStats = (vehicleData) => {
  if (!vehicleData) return null;

  const { battery, motor, vehicle } = vehicleData;

  // Battery Statistics
  const getBatteryStats = () => ({
    soc: {
      value: battery?.soc || 0,
      formatted: `${battery?.soc?.toFixed(1) || '0.0'}%`,
      label: 'SOC',
      fullLabel: 'State of Charge',
      icon: '⚡',
      unit: '%'
    },
    soh: {
      value: battery?.soh || 0,
      formatted: `${battery?.soh || 0}%`,
      label: 'SOH',
      fullLabel: 'State of Health',
      icon: '🔋',
      unit: '%'
    },
    range: {
      value: typeof battery?.range === 'object'
        ? battery?.range?.estimated
        : battery?.range || 0,
      actual: typeof battery?.range === 'object'
        ? battery?.range?.actual
        : null,
      formatted: `${typeof battery?.range === 'object'
        ? battery?.range?.estimated
        : battery?.range || 0} km`,
      label: 'RANGE',
      fullLabel: 'Estimated Range',
      icon: '🔋',
      unit: 'km'
    },
    temperature: {
      pack: battery?.temperature?.pack || 0,
      min: battery?.temperature?.min || 0,
      max: battery?.temperature?.max || 0,
      formatted: `${battery?.temperature?.pack?.toFixed(1) || '0.0'}°C`,
      label: 'TEMP',
      fullLabel: 'Battery Temperature',
      icon: '🌡️',
      unit: '°C'
    },
    voltage: {
      value: battery?.voltage || 0,
      formatted: `${battery?.voltage?.toFixed(1) || '0.0'}V`,
      label: 'VOLTAGE',
      fullLabel: 'Pack Voltage',
      icon: '⚡',
      unit: 'V'
    },
    current: {
      value: battery?.current || 0,
      formatted: `${battery?.current?.toFixed(1) || '0.0'}A`,
      label: 'CURRENT',
      fullLabel: 'Pack Current',
      icon: '⚡',
      unit: 'A'
    },
    cells: {
      count: battery?.cells?.count || 0,
      avgVoltage: battery?.cells?.avgVoltage || 0,
      minVoltage: battery?.cells?.minVoltage || 0,
      maxVoltage: battery?.cells?.maxVoltage || 0,
      imbalance: battery?.cells?.imbalance || 0,
      formattedAvg: `${battery?.cells?.avgVoltage?.toFixed(3) || '0.000'}V`,
      formattedImbalance: `${battery?.cells?.imbalance?.toFixed(3) || '0.000'}V`
    },
    capacity: {
      total: battery?.capacity?.total || 0,
      available: battery?.capacity?.available || 0,
      nominal: battery?.capacity?.nominal || battery?.capacity || 0,
      formatted: `${battery?.capacity?.nominal || battery?.capacity || 0} kWh`,
      unit: 'kWh'
    }
  });

  // Motor Statistics
  const getMotorStats = () => ({
    temperature: {
      value: motor?.temperature || 0,
      formatted: `${motor?.temperature?.toFixed(1) || '0.0'}°C`,
      label: 'MOTOR TEMP',
      fullLabel: 'Motor Temperature',
      icon: '⚙️',
      unit: '°C'
    },
    status: {
      value: motor?.status || 'idle',
      formatted: (motor?.status || 'idle').toUpperCase(),
      label: 'STATUS',
      icon: '⚙️'
    },
    efficiency: {
      value: motor?.efficiency || 0,
      formatted: `${motor?.efficiency?.toFixed(1) || '0.0'}%`,
      label: 'EFFICIENCY',
      icon: '♻️',
      unit: '%'
    },
    rpm: {
      value: motor?.rpm || 0,
      formatted: `${motor?.rpm?.toLocaleString() || '0'} RPM`,
      label: 'RPM',
      icon: '⚙️',
      unit: 'RPM'
    },
    specs: {
      maxPower: motor?.specs?.maxPower || 0,
      maxTorque: motor?.specs?.maxTorque || 0,
      maxRpm: motor?.specs?.maxRpm || 0
    }
  });

  // Vehicle Statistics
  const getVehicleStats = () => ({
    location: {
      city: vehicle?.location?.city || 'Unknown',
      formatted: vehicle?.location?.city || 'Unknown',
      icon: '📍'
    },
    odometer: {
      value: vehicle?.odometer || 0,
      formatted: `${vehicle?.odometer?.toLocaleString() || '0'} km`,
      label: 'ODOMETER',
      unit: 'km'
    }
  });

  // Health indicators with color coding
  const getHealthColor = (value, type = 'percentage') => {
    if (type === 'percentage') {
      if (value >= 80) return '#10b981'; // Green
      if (value >= 60) return '#f59e0b'; // Yellow
      return '#ef4444'; // Red
    }
    if (type === 'temperature') {
      if (value < 35) return '#10b981'; // Green
      if (value < 45) return '#f59e0b'; // Yellow
      return '#ef4444'; // Red
    }
    return '#66B4C4'; // Default accent
  };

  return {
    battery: getBatteryStats(),
    motor: getMotorStats(),
    vehicle: getVehicleStats(),
    getHealthColor
  };
};

export default useVehicleStats;
